import { mstCommon } from '@libs/domain';
import type {
  BaseInputStringInFieldBodyProps,
  ComponentFieldProps,
  InputField,
  InputWithExclusionProps,
  PageProps,
} from '@ps-smart-cp/ppfw-client';
import {
  GridD,
  MasterSearchGridButtonGroup,
  SearchDialog,
  getNameWithDefault,
  useGridD,
  useLocalizedContents,
} from '@ps-smart-cp/ppfw-client';
import { useState } from 'react';
import { OutputOrderField, SearchBasicInfo } from '../components';
import { useMstListSearch } from '../hooks';
import type { MstListSearch, MstRegistration } from '../models';
import { OutputOrder } from '../models';
import type { ListRequestFunction } from '../repositories';
import type { BaseMstSearchTmplProps } from './MstTmpl.type';

interface MstSearchProps<T, K extends keyof T, I>
  extends ComponentFieldProps<I>,
    Omit<BaseInputStringInFieldBodyProps<T, K>, 'extraBlurHandler' | 'type'>,
    InputField<string> {}

interface MstRangeSearchProps<T, K extends keyof T, I>
  extends ComponentFieldProps<I>,
    Omit<InputWithExclusionProps<T, K>, 'itemAttribute' | 'type'> {}

interface MstSearchDialogProps<R extends MstRegistration = MstRegistration> {
  setIsOpen: (v: boolean) => void;
  setMaster: (code: string, name: string) => void;
  getMaster: (code: string) => Promise<R>;
  doWithResponse?: (res: R) => void;
}

interface MstSearchDialogTmplProps<S extends MstListSearch, R extends MstRegistration>
  extends MstSearchDialogProps<R>,
    BaseMstSearchTmplProps<S, R> {}

function MstSearchDialogTmpl<S extends MstListSearch, R extends MstRegistration = MstRegistration>({
  mstName,
  items,
  parsers,
  columns,
  listRequestFunction,
  initMst,
  children,
  rerender,
  ...restProps
}: MstSearchDialogTmplProps<S, R> & PageProps) {
  const { locale } = useLocalizedContents();
  const mstSearch = useMstListSearch(initMst, items, parsers);
  const [outputOrder, setOutputOrder] = useState(OutputOrder.init);
  const { search, ...gridParams } = useGridD<R, ListRequestFunction<MstListSearch, R>>(
    30,
    listRequestFunction as ListRequestFunction<MstListSearch, R>,
    initMst(),
    OutputOrder.init(),
  );

  const onSearch = async () => {
    try {
      await search(mstSearch.mstSearch, outputOrder);
    } catch (e) {
      console.debug(e);
    }
  };

  return (
    <SearchDialog
      title={
        getNameWithDefault(mstName.displayName, locale) +
        getNameWithDefault(mstCommon.titleNames.search.displayName, locale)
      }
      selectedRows={gridParams.selectedRows}
      gridSchema={gridParams.gridSchema}
      {...restProps}
    >
      <SearchBasicInfo {...mstSearch}>{children}</SearchBasicInfo>
      <OutputOrderField outputKey={outputOrder} setOutputKey={setOutputOrder} />
      <MasterSearchGridButtonGroup onSearch={onSearch} onReset={rerender} />
      <GridD columns={columns} nonRowsFallback="message" {...gridParams} />
    </SearchDialog>
  );
}

export { MstSearchDialogTmpl };
export type { MstRangeSearchProps, MstSearchDialogProps, MstSearchDialogTmplProps, MstSearchProps };
